/**
 * P2.3 — URL serialisation for Compass Explorer state.
 *
 * Round-trips three things across a refresh or paste-share:
 *   1. Active lens id  (`?lens=`)
 *   2. Search query   (`?q=`)
 *   3. Filter facets  (`?f=tier:compute,env:prod`)
 *   4. Focus + zoom   (`?focus=vpc:vpc-1&z=3`)
 *
 * The filter encoding is intentionally compact because Compass users
 * paste these URLs into Slack / PDF deliverables. It compresses a
 * primary-filter `ViewRule`'s conditions into a comma list where each
 * entry is `field:value` for `equals` / `contains`, or
 * `field:@empty` / `field:>N` for the other operators. Operators not
 * in this shortlist fall through to a JSON blob under `?fj=`.
 *
 * Pure functions — no React, no store reads. Callers wire them at the
 * route handler: read URL → write to prefs + viewRulesStore on mount,
 * write URL on prefs/rules change.
 */

import type { ViewRule, ViewCondition } from '../../../../core/assistant/models/ViewRules';
import {
  PRIMARY_FILTER_ID,
  createPrimaryFilterRule,
} from '../../../../core/assistant/models/ViewRules';
import type {
  FocusSpec,
  ZoomLevel,
} from '../../../../stores/compassViewPrefsStore';

const PRESET_LABELS: Record<string, string> = {
  'attack-surface': 'Attack Surface',
  'public-exposure': 'Public Exposure',
  'public-path-to-data': 'Public Path to Data',
  'drift-from-baseline': 'Drift from Baseline',
  'planned-changes': 'Planned Changes',
  'cost-waste': 'Cost Waste',
  'ownership-gaps': 'Ownership Gaps',
  'compute-fleet': 'Compute Fleet',
  'data-stores': 'Data Stores',
  'iam-surface': 'IAM Surface',
};

export interface CompassUrlState {
  readonly lensId?: string;
  readonly searchQuery?: string;
  readonly filters?: readonly FilterFacet[];
  readonly filterJson?: string;
  readonly focus?: FocusSpec;
  readonly zoom?: ZoomLevel;
  /**
   * Active quick-filter chip ids (cost, waste, findings, public,
   * stale, unowned, drift, cross-account, production). Encoded as a
   * comma-separated list under `chips=`. Consumed by the hydration
   * effect in `useCompassExplorerSync`.
   */
  readonly chipIds?: readonly string[];
}

export interface FilterFacet {
  readonly field: string;
  readonly value: string;
  /** Operator shorthand; defaults to `equals`. */
  readonly op?: 'equals' | 'contains' | 'is_empty' | 'is_not_empty' | 'greater_than' | 'less_than';
}

// ── Encode ──────────────────────────────────────────────────────

export function encodeCompassUrlState(state: CompassUrlState): string {
  const parts: [string, string][] = [];
  if (state.lensId) parts.push(['lens', state.lensId]);
  if (state.searchQuery) parts.push(['q', state.searchQuery]);
  if (state.filters && state.filters.length > 0) {
    parts.push(['f', state.filters.map(encodeFacet).join(',')]);
  }
  if (state.filterJson) parts.push(['fj', state.filterJson]);
  if (state.focus && state.focus.kind !== 'overview') {
    parts.push(['focus', encodeFocus(state.focus)]);
  }
  if (state.zoom !== undefined) parts.push(['z', String(state.zoom)]);
  if (state.chipIds && state.chipIds.length > 0) {
    parts.push(['chips', state.chipIds.join(',')]);
  }
  if (parts.length === 0) return '';
  return parts
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join('&');
}

function encodeFacet(f: FilterFacet): string {
  switch (f.op ?? 'equals') {
    case 'equals':
      return `${f.field}:${f.value}`;
    case 'contains':
      return `${f.field}~${f.value}`;
    case 'is_empty':
      return `${f.field}:@empty`;
    case 'is_not_empty':
      return `${f.field}:@present`;
    case 'greater_than':
      return `${f.field}:>${f.value}`;
    case 'less_than':
      return `${f.field}:<${f.value}`;
  }
}

function encodeFocus(focus: FocusSpec): string {
  switch (focus.kind) {
    case 'account':
      return `account:${focus.accountId}`;
    case 'vpc':
      return `vpc:${focus.vpcId}`;
    case 'subnet':
      return `subnet:${focus.subnetId}`;
    case 'resource':
      return `resource:${focus.resourceId}`;
    case 'preset':
      return `preset:${focus.presetId}`;
    case 'overview':
      return 'overview';
  }
}

// ── Decode ──────────────────────────────────────────────────────

export function decodeCompassUrlState(search: string): CompassUrlState {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const out: Mutable<CompassUrlState> = {};
  const lens = params.get('lens');
  if (lens) out.lensId = lens;
  const q = params.get('q');
  if (q) out.searchQuery = q;
  const fs = params.get('f');
  if (fs) out.filters = fs.split(',').map(decodeFacet).filter((x): x is FilterFacet => x !== null);
  const fj = params.get('fj');
  if (fj) out.filterJson = fj;
  const focus = params.get('focus');
  if (focus) {
    const f = decodeFocus(focus);
    if (f) out.focus = f;
  }
  const z = params.get('z');
  if (z) {
    const n = Number(z);
    if (Number.isInteger(n) && n >= 1 && n <= 5) out.zoom = n as ZoomLevel;
  }
  return out;
}

function decodeFacet(raw: string): FilterFacet | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  // Operator tokens: `>`, `<`, `~`, `:`
  const gtIdx = trimmed.indexOf(':>');
  if (gtIdx > 0) return { field: trimmed.slice(0, gtIdx), op: 'greater_than', value: trimmed.slice(gtIdx + 2) };
  const ltIdx = trimmed.indexOf(':<');
  if (ltIdx > 0) return { field: trimmed.slice(0, ltIdx), op: 'less_than', value: trimmed.slice(ltIdx + 2) };
  const tilde = trimmed.indexOf('~');
  if (tilde > 0) return { field: trimmed.slice(0, tilde), op: 'contains', value: trimmed.slice(tilde + 1) };
  const colon = trimmed.indexOf(':');
  if (colon <= 0) return null;
  const field = trimmed.slice(0, colon);
  const value = trimmed.slice(colon + 1);
  if (value === '@empty') return { field, op: 'is_empty', value: '' };
  if (value === '@present') return { field, op: 'is_not_empty', value: '' };
  return { field, value, op: 'equals' };
}

function decodeFocus(raw: string): FocusSpec | null {
  if (raw === 'overview') return { kind: 'overview' };
  const [kind, id] = raw.split(':', 2);
  if (!id) return null;
  switch (kind) {
    case 'account':
      return { kind: 'account', accountId: id };
    case 'vpc':
      return { kind: 'vpc', vpcId: id };
    case 'subnet':
      return { kind: 'subnet', subnetId: id };
    case 'resource':
      return { kind: 'resource', resourceId: id };
    case 'preset':
      return { kind: 'preset', presetId: id, label: PRESET_LABELS[id] };
    default:
      return null;
  }
}

// ── ViewRule <-> facet bridges ────────────────────────────────

/**
 * Convert a `viewRulesStore` primary-filter rule into URL facets.
 * Unsupported operators fall into the returned `overflow` array so the
 * caller can emit them under `?fj=<json>`.
 */
export function facetsFromPrimaryFilter(rule: ViewRule): {
  facets: FilterFacet[];
  overflow: ViewCondition[];
} {
  const facets: FilterFacet[] = [];
  const overflow: ViewCondition[] = [];
  for (const cond of rule.conditions) {
    const facet = facetFromCondition(cond);
    if (facet) facets.push(facet);
    else overflow.push(cond);
  }
  return { facets, overflow };
}

function facetFromCondition(cond: ViewCondition): FilterFacet | null {
  const value = cond.value;
  switch (cond.operator) {
    case 'equals':
    case 'not_equals':
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return { field: cond.field, op: 'equals', value: String(value) };
      }
      return null;
    case 'contains':
      if (typeof value === 'string') return { field: cond.field, op: 'contains', value };
      return null;
    case 'is_empty':
      return { field: cond.field, op: 'is_empty', value: '' };
    case 'is_not_empty':
      return { field: cond.field, op: 'is_not_empty', value: '' };
    case 'greater_than':
      if (typeof value === 'number') return { field: cond.field, op: 'greater_than', value: String(value) };
      return null;
    case 'less_than':
      if (typeof value === 'number') return { field: cond.field, op: 'less_than', value: String(value) };
      return null;
    default:
      return null;
  }
}

/**
 * Rebuild a primary-filter rule from URL facets. Returns the same
 * shape `createPrimaryFilterRule()` emits so callers can drop it into
 * `viewRulesStore.rules`.
 */
export function primaryFilterFromFacets(facets: readonly FilterFacet[]): ViewRule {
  const rule = {
    ...createPrimaryFilterRule(),
    id: PRIMARY_FILTER_ID,
  } as ViewRule;
  rule.conditions = facets.map((f, i) => ({
    id: `f-${i}`,
    field: f.field,
    operator: mapFacetOpToOperator(f.op ?? 'equals'),
    value: coerceValue(f),
  }));
  return rule;
}

function mapFacetOpToOperator(op: FilterFacet['op']): ViewCondition['operator'] {
  switch (op) {
    case 'equals':
      return 'equals';
    case 'contains':
      return 'contains';
    case 'is_empty':
      return 'is_empty';
    case 'is_not_empty':
      return 'is_not_empty';
    case 'greater_than':
      return 'greater_than';
    case 'less_than':
      return 'less_than';
    default:
      return 'equals';
  }
}

function coerceValue(f: FilterFacet): ViewCondition['value'] {
  if (f.op === 'is_empty' || f.op === 'is_not_empty') return null;
  if (f.op === 'greater_than' || f.op === 'less_than') {
    const n = Number(f.value);
    return Number.isFinite(n) ? n : f.value;
  }
  if (f.value === 'true') return true;
  if (f.value === 'false') return false;
  const n = Number(f.value);
  if (Number.isFinite(n) && f.value.trim() !== '' && !isNaN(n)) return n;
  return f.value;
}

type Mutable<T> = { -readonly [K in keyof T]: T[K] };
